import React from 'react';
import Form from './Form';

import AddIcon from '@material-ui/icons/Add';
import { DataTable, toast, Button as DsButton, PageTitle, FilterSelect, Tag, toneForVinculo, criarCopiaDeColuna } from '../../components/ds';
import AppState from '../../state';
import { browserHistory } from 'react-router';
import { Utils } from '../../services';
import * as funcionarioAPI from '../../api/funcionarios';
import CboAprendizModal from './CboAprendizModal';
import ReactExport from 'react-data-export';
import MenuItem from '@material-ui/core/MenuItem';
const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

// ---------------------------------------------------------------------------
// Cota de jovem aprendiz (CLT art. 429 e Decreto 10.854/2021)
// ---------------------------------------------------------------------------
// A Ambiental tem um estabelecimento só (sem filiais), então a conta é feita
// sobre o cadastro inteiro.
//
// Base = CLT ativos, sem os próprios aprendizes, cujo CBO (ocupação, 6
// dígitos) está marcado como "Entra" na lista de CBOs do jovem aprendiz
// (botão "CBOs do jovem aprendiz", tabela cbo_aprendiz). Só a lista decide:
// CBO fora dela não conta. Temporários e terceirizados não estão no cadastro
// como CLT, então já ficam de fora pelo vínculo.
//
// A lista existe para tirar da base, pela FUNÇÃO e não pela formação da
// pessoa, direção, gerência e confiança e as funções que exigem nível
// superior ou técnico regulamentado. O DP confere no site da CBO, no campo
// "Formação e experiência" de cada ocupação.
//
// Mínimo de 5% e máximo de 15% da base. Qualquer fração arredonda para cima
// no mínimo (5% de 75 = 3,75 → 4). Abaixo de 7 elegíveis não há obrigação.
const COTA_APRENDIZ_MIN = 0.05;
const COTA_APRENDIZ_MAX = 0.15;
const COTA_APRENDIZ_MINIMO_ELEGIVEIS = 7;

const semAcento = (v) =>
  String(v || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();

// Sem campo próprio no cadastro, o aprendiz é reconhecido pelo cargo.
const ehAprendiz = (f) => semAcento(f.cargo).includes('APRENDIZ');

const cboDigitos = (f) => String(f.cbo || '').replace(/\D/g, '');

// regras: linhas de GetCboAprendiz.php ({cbo, entra_calculo}). Devolve o
// conjunto dos CBOs que entram.
const cbosQueEntram = (regras) =>
  new Set(
    (regras || [])
      .filter((r) => r.entra_calculo === true || r.entra_calculo === 't')
      .map((r) => String(r.cbo).trim())
  );

// Conta sobre quem está ativo hoje, independente do filtro da tela.
const calcularCotaAprendiz = (funcionarios, regras) => {
  const entram = cbosQueEntram(regras);
  const ativos = funcionarios.filter((f) => f.status === 'ativo');
  const aprendizes = ativos.filter(ehAprendiz).length;
  const clt = ativos.filter(
    (f) => semAcento(f.tipo_vinculo).trim() === 'CLT' && !ehAprendiz(f)
  );
  const semCbo = clt.filter((f) => cboDigitos(f).length !== 6).length;
  const base = clt.filter((f) => entram.has(cboDigitos(f))).length;
  const foraDaLista = clt.length - semCbo - base;
  const obrigada = base >= COTA_APRENDIZ_MINIMO_ELEGIVEIS;
  return {
    aprendizes,
    base,
    semCbo,
    foraDaLista,
    obrigada,
    minimo: obrigada ? Math.ceil(base * COTA_APRENDIZ_MIN) : 0,
    maximo: Math.floor(base * COTA_APRENDIZ_MAX)
  };
};

class Funcionarios extends React.Component {
  constructor(props) {
    super(props);
    // Cópia por coluna: o módulo guarda as linhas visíveis e devolve os
    // helpers de cabeçalho e coluna já ligados a elas.
    this.copia = criarCopiaDeColuna();

    this.state = {
      dialog: false,
      funcionarios: [],
      funcionario: '',
      edicao: false,
      statusFilter: 'ativo',
      // Quem está perto de estourar o prazo de férias, indexado por CPF
      // (ver GetAlertasFerias.php). Sem férias registradas, ninguém é marcado.
      alertasFerias: {},
      // Lista de CBOs da cota de jovem aprendiz (null = não carregou).
      regrasCbo: null,
      cboModalAberto: false,
    };
    this.getTableData();
    this.carregarAlertasFerias();
    this.carregarRegrasCbo();
  }

  carregarRegrasCbo = () => {
    funcionarioAPI
      .getCboAprendiz()
      .then((r) => this.setState({ regrasCbo: (r && r.request) || [] }))
      .catch(() => {
        // Sem a lista (migração não rodou), o selo avisa em vez de calcular.
        this.setState({ regrasCbo: null });
      });
  };

  carregarAlertasFerias = () => {
    funcionarioAPI
      .getAlertasFerias()
      .then((r) => {
        const porCpf = {};
        ((r && r.request) || []).forEach((a) => {
          porCpf[a.cpf_funcionario] = a;
        });
        this.setState({ alertasFerias: porCpf });
      })
      .catch(() => {
        // Aviso é complementar: se falhar, a lista continua funcionando.
      });
  };

  getTableData = () => {
    AppState.isLoading = true;
    fetch(
      `${AppState.serverAddr}/funcionarios/GetFuncionarios.php`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          Authorization: AppState.token,
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          return response.json().then((json) => {
            let error = new Error(response.status);
            error.response = json;
            throw error;
          });
        } else {
          AppState.isLoading = false;
          const res = response.json();
          return res;
        }
      })
      .then((response) => {
        AppState.isLoading = false;
        this.setState({
          funcionarios: response.request,
        });
      })
      .catch(function (error) {
        AppState.isLoading = false;
        if (error.toString() == '' || error.toString().split(' ').length < 2) {
          toast.error('Erro desconhecido, tente novamente');
          return;
        }
        var Err_status = error.toString().split(' ')[1];
        if (Err_status == 400) {
          var ret = error.response['status'].value.split(' ')[0];
          if (ret == '40001') {
            console.log('40001 Invalid JSON value found');
          } else toast.error('Dados inválidos');
        } else if (Err_status == 401) {
          browserHistory.push(`${process.env.PUBLIC_URL}/logout`);
        } else if (Err_status == 500) {
          toast.error('Erro desconhecido, tente novamente');
        }
      });
  };

  closeDialog = () => {
    this.setState({
      dialog: false,
      edicao: false,
    });
  };

  // Cadastro novo abre na página, no mesmo layout em cards da ficha.
  openDialog = () => {
    browserHistory.push(`${process.env.PUBLIC_URL}/funcionarios/novo`);
  };

  closeDialog = () => {
    this.setState({
      dialog: false,
      edicao: false,
    });
  };

  preparaDado = (dados) => {
    if (dados == null || dados.length < 1) return [];
    // Cópia de cada linha, não só do array: antes o slice era raso e a
    // formatação para o Excel alterava os mesmos objetos que a tabela na tela
    // usa — exportar reformatava as datas já formatadas da listagem.
    let ret = dados.map((linha) => Object.assign({}, linha));
    for (let i = 0; i < dados.length; i++) {
      ret[i].dt_nascimento = Utils.dateToPT(dados[i].dt_nascimento);
      ret[i].dt_admissao = Utils.dateToPT(dados[i].dt_admissao);
      ret[i].dt_demissao = Utils.dateToPT(dados[i].dt_demissao);
      ret[i].rg_dt_emissao = Utils.dateToPT(dados[i].rg_dt_emissao);
      ret[i].ctps_data = Utils.dateToPT(dados[i].ctps_data);
      ret[i].cnh_dt_emissao = Utils.dateToPT(dados[i].cnh_dt_emissao);
      ret[i].timestamp_criacao = Utils.dateToPT(dados[i].timestamp_criacao);
      ret[i].ultima_atualizacao = Utils.dateToPT(dados[i].ultima_atualizacao);
      ret[i].status = dados[i].status == 'ativo' ? 'Ativo' : 'Desligado';
      ret[i].sexo = dados[i].sexo == 'M' ? 'Masculino' : 'Feminino';
      // Deficiência e benefícios do contrato, em texto para a planilha.
      const sim = (v) => v === true || v === 't' || v === 'true';
      ret[i].pcd_texto = sim(dados[i].pcd) ? 'Sim' : 'Não';
      const tiposPcd = {
        fisica: 'Física', auditiva: 'Auditiva', visual: 'Visual', intelectual: 'Intelectual',
        mental: 'Mental', multipla: 'Múltipla', reabilitado: 'Reabilitado (INSS)',
      };
      ret[i].pcd_tipos_texto = sim(dados[i].pcd)
        ? [tiposPcd[dados[i].pcd_tipo] || dados[i].pcd_tipo, dados[i].pcd_descricao].filter(Boolean).join(' — ')
        : '';
      ret[i].beneficios_texto = [
        ['tem_vale_transporte', 'Vale-transporte'],
        ['tem_vale_alimentacao', 'Vale-alimentação'],
        ['tem_plano_saude', 'Plano de saúde'],
        ['tem_seguro_vida', 'Seguro de vida'],
        ['tem_auxilio_creche', 'Auxílio-creche'],
      ]
        .filter(([coluna]) => sim(dados[i][coluna]))
        .map(([, rotulo]) => rotulo)
        .join(', ');
      ret[i].custo_total_mensal =
        'R$ ' + Utils.float2moeda(dados[i].custo_total_mensal);
    }
    return ret;
  };

  render() {
    const { statusFilter, funcionarios } = this.state;
    const filteredFuncionarios =
      statusFilter === 'todos'
        ? funcionarios
        : funcionarios.filter((f) =>
            statusFilter === 'ativo' ? f.status === 'ativo' : f.status !== 'ativo'
          );


    const homens = filteredFuncionarios.filter((f) => f.sexo === 'M').length;
    const mulheres = filteredFuncionarios.filter((f) => f.sexo === 'F').length;
    const total = filteredFuncionarios.length;
    const pct = (n) => (total ? Math.round((n / total) * 100) : 0);

    const regrasCarregadas = this.state.regrasCbo !== null;
    const cota = calcularCotaAprendiz(funcionarios, this.state.regrasCbo);
    const cotaOk = cota.aprendizes >= cota.minimo;
    const foraDaConta =
      (cota.semCbo > 0 ? ` ${cota.semCbo} pessoa(s) sem CBO de 6 dígitos ficaram fora da conta.` : '') +
      (cota.foraDaLista > 0 ? ` ${cota.foraDaLista} pessoa(s) com CBO fora da lista ou marcado "Não entra".` : '');
    const dicaCota = !regrasCarregadas
      ? 'Lista de CBOs do jovem aprendiz não carregou (a migração 2026-10-16-cbo-jovem-aprendiz.sql rodou?).'
      : cota.obrigada
      ? `Base: ${cota.base} CLT ativos com CBO que entra no cálculo. ` +
        `Mínimo 5% = ${cota.minimo}, máximo 15% = ${cota.maximo}.` + foraDaConta
      : `Base de ${cota.base} funções elegíveis: abaixo de 7 não há obrigação.` + foraDaConta;

    const filters = (
    <>
      <FilterSelect
        value={statusFilter}
        onChange={(e) => this.setState({ statusFilter: e.target.value })}
      >
        <MenuItem value="ativo">Ativo</MenuItem>
        <MenuItem value="inativo">Desligado</MenuItem>
        <MenuItem value="todos">Todos</MenuItem>
      </FilterSelect>

      <span style={{ marginLeft: 16, fontSize: '0.9em', color: '#666' }}>
        {pct(homens)}% homens {pct(mulheres)}% mulheres
      </span>

      <span style={{ marginLeft: 16 }} title={dicaCota}>
        <Tag tone={!regrasCarregadas ? 'grey' : !cota.obrigada || cotaOk ? 'green' : 'red'} dot={false}>
          {!regrasCarregadas
            ? 'Cota de aprendiz: lista de CBOs indisponível'
            : cota.obrigada
            ? `${cota.aprendizes} aprendizes ${cotaOk ? '≥' : '<'} ${cota.minimo} necessários`
            : 'Cota de aprendiz não obrigatória'}
        </Tag>
        {cota.semCbo > 0 ? (
          <span style={{ marginLeft: 8, fontSize: '0.85em', color: '#999' }}>
            ({cota.semCbo} sem CBO)
          </span>
        ) : null}
      </span>
    </>
    );

    const actions = (
      <>
        <DsButton
          intent="primary"
          onClick={this.openDialog}
        >
          <AddIcon />
          Novo funcionário
        </DsButton>

        <DsButton
          intent="outlined"
          onClick={() => this.setState({ cboModalAberto: true })}
        >
          CBOs do jovem aprendiz
        </DsButton>

        <ExcelFile
              filename={'RelatorioFuncionarios-' + Utils.getTodayDate()}
              element={
                <DsButton
                  intent="outlined"
                  onClick={this.exportarExcel}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    x="0px"
                    y="0px"
                    width="24"
                    height="24"
                    viewBox="0 0 192 192"
                    style={{ fill: '#000000', paddingRight: '6px' }}
                  >
                    <g
                      fill="none"
                      fillRule="nonzero"
                      stroke="none"
                      strokeWidth="1"
                      strokeLinecap="butt"
                      strokeLinejoin="miter"
                      strokeMiterlimit="10"
                      strokeDasharray=""
                      strokeDashoffset="0"
                      fontFamily="none"
                      fontWeight="none"
                      fontSize="none"
                      textAnchor="none"
                      style={{ mixBlendMode: 'normal' }}
                    >
                      <path d="M0,192v-192h192v192z" fill="none"></path>
                      <g fill="#1a1a1a">
                        <g id="surface1">
                          <path d="M110.88,0c-0.075,0.03 -0.165,0.075 -0.24,0.12l-107.52,20.4c-1.83,0.345 -3.15,1.965 -3.12,3.84v143.28c-0.03,1.875 1.29,3.495 3.12,3.84l107.52,20.4c1.11,0.21 2.265,-0.075 3.15,-0.795c0.87,-0.72 1.395,-1.785 1.41,-2.925v-19.2h65.28c4.2,0 7.68,-3.48 7.68,-7.68v-130.56c0,-4.2 -3.48,-7.68 -7.68,-7.68h-65.28v-19.2c0.015,-1.11 -0.465,-2.16 -1.29,-2.895c-0.825,-0.735 -1.935,-1.08 -3.03,-0.945zM107.52,8.4v16.68c-0.51,1.065 -0.51,2.295 0,3.36v135.96c-0.105,0.51 -0.105,1.05 0,1.56v17.64l-99.84,-19.08v-137.04zM115.2,30.72h65.28v130.56h-65.28v-19.2h15.36v-7.68h-15.36v-23.04h15.36v-7.68h-15.36v-19.2h15.36v-7.68h-15.36v-19.2h15.36v-7.68h-15.36zM138.24,49.92v7.68h30.72v-7.68zM25.68,60.24l21,35.88l-22.92,35.88h19.2l12.48,-23.16c0.87,-2.235 1.44,-3.945 1.68,-5.04h0.12c0.495,2.34 0.975,3.93 1.44,4.8l12.48,23.4h19.08l-22.08,-36.24l21.48,-35.52h-18l-11.4,21.24c-1.095,2.775 -1.875,4.965 -2.28,6.36h-0.12c-0.63,-2.34 -1.35,-4.425 -2.16,-6.12l-10.32,-21.48zM138.24,76.8v7.68h30.72v-7.68zM138.24,103.68v7.68h30.72v-7.68zM138.24,134.4v7.68h30.72v-7.68z"></path>
                        </g>
                      </g>
                    </g>
                  </svg>
                  Exportar
                </DsButton>
              }
            >
              <ExcelSheet
                // Exporta o que a tabela está mostrando: filtro de status,
                // busca e ordenação aplicados, todas as páginas.
                data={() => this.preparaDado(this.copia.linhas())}
                name="Funcionarios"
              >
                <ExcelColumn
                  label="CPF Funcionário"
                  value="cpf_funcionario"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Matricula"
                  value="matricula_funcionario"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Nome"
                  value="nome_funcionario"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Sexo"
                  value="sexo"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Nacionalidade"
                  value="nacionalidade"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Data de Nascimento"
                  value="dt_nascimento"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Cidade Nascimento"
                  value="cidade_nascimento"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Nome do pai"
                  value="nome_pai"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Nome da Mãe"
                  value="nome_mae"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Estado Civil"
                  value="estado_civil"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Celular"
                  value="celular"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Tel. Residência"
                  value="tel_residencia"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="E-mail Pessoal"
                  value="email_pessoal"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="E-mail Profissional"
                  value="email_profissional"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Data de Admissão"
                  value="dt_admissao"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Data de Demissão"
                  value="dt_demissao"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="RG"
                  value="rg_numero"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="RG Dt. Emissão"
                  value="rg_dt_emissao"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="RG Emissor"
                  value="rg_emissor"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="RG UF"
                  value="rg_uf"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="CTPS Número"
                  value="ctps_numero"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="CTPS Série"
                  value="ctps_serie"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="CTPS Data"
                  value="ctps_data"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="CTPS UF"
                  value="ctps_uf"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Certif. Alistamento Militar"
                  value="cert_alistamento_militar"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="PIS Número"
                  value="pis_numero"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="CNH Número"
                  value="cnh_numero"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="CNH Categoria"
                  value="cnh_categoria"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="CNH Dt. Emissão"
                  value="cnh_dt_emissao"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="CNH Dt. Vencimento"
                  value="cnh_dt_vencimento"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Título Eleitor"
                  value="titulo_eleitor"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Título Zona"
                  value="titulo_zona"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Título Sessão"
                  value="titulo_sessao"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="CEP Residência"
                  value="cep_residencia"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Endereço Residência"
                  value="end_residencia"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Número Residência"
                  value="numero_residencia"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Complemento Residência"
                  value="complemento_residencia"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Cidade Residência"
                  value="cidade_residencia"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="UF Residência"
                  value="uf_residencia"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Tipo de Vínculo"
                  value="tipo_vinculo"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Criado Em"
                  value="timestamp_criacao"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Jornada Diária"
                  value="jornada_diaria_trabalho"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="ID Projeto Vinculado"
                  value="id_projeto_vinculado"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Custo Mensal"
                  value="custo_total_mensal"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Bairro Residência"
                  value="bairro_residencia"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Cargo"
                  value="cargo"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Link Documentação"
                  value="link_documentacao"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="PcD"
                  value="pcd_texto"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Tipo de deficiência"
                  value="pcd_tipos_texto"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Benefícios"
                  value="beneficios_texto"
                  style={{ font: { sz: '24', bold: true } }}
                />
                <ExcelColumn
                  label="Status"
                  value="status"
                  style={{ font: { sz: '24', bold: true } }}
                />
              </ExcelSheet>
            </ExcelFile>
      </>
    );

    return (
      <div id={this.constructor.name}>
        <PageTitle>Cadastro de Funcionário</PageTitle>

        <DataTable
          actions={actions}
          filters={filters}
          data={filteredFuncionarios}
          defaultPageSize={20}
          className="ds-table -highlight ds-table--scroll-x"
          getTableProps={this.copia.capturarLinhasVisiveis}
          columns={[
            {
              Header: 'Nº',
              id: 'numero',
              width: 60,
              sortable: false,
              Cell: (row) => (
                <div className="action-cell" style={{ textAlign: 'center' }}>
                  {row.index + 1}
                </div>
              ),
            },
            this.copia.coluna('Registro', 'matricula_funcionario', { width: 110 }),
            this.copia.coluna('Cód. Meridional', 'cod_meridional', { width: 140 }),
            this.copia.coluna('Projeto Atual', 'projeto_atual', { width: 180 }),
            {
              Header: this.copia.cabecalho('Nome', 'nome_funcionario'),
              accessor: 'nome_funcionario',
              width: 260,
              Cell: (row) => (
                <span
                  className="action-cell"
                  style={{
                    color: '#2f6fa8',
                    cursor: 'pointer',
                    fontWeight: 500,
                  }}
                  onClick={() =>
                    browserHistory.push(
                      `${process.env.PUBLIC_URL}/funcionarios/${row.original['cpf_funcionario']}`
                    )
                  }
                >
                  {row.value}
                </span>
              ),
            },
            this.copia.coluna('Cargo', 'cargo', { width: 200 }),
            this.copia.coluna('CBO', 'cbo', { width: 100 }),
            this.copia.coluna('Dt. Admissão', 'dt_admissao', {
              width: 130,
              formatar: (v) => (v ? Utils.dateToPT(v) : ''),
            }),
            this.copia.coluna('Dt. Nascimento', 'dt_nascimento', {
              width: 140,
              formatar: (v) => (v ? Utils.dateToPT(v) : ''),
            }),
            this.copia.coluna('Sexo', 'sexo', { width: 90 }),
            this.copia.coluna('Escolaridade', 'escolaridade', { width: 160 }),
            this.copia.coluna('Raça e cor', 'raca_cor', { width: 140 }),
            this.copia.coluna('Sindicato', 'sindicato', { width: 180 }),
            this.copia.coluna('Estado', 'uf_residencia', { width: 90 }),
            this.copia.coluna('Telefone pessoal', 'celular', { width: 150 }),
            this.copia.coluna('Telefone Ambiental', 'tel_residencia', { width: 160 }),
            this.copia.coluna('E-mail pessoal', 'email_pessoal', { width: 240 }),
            this.copia.coluna('E-mail Ambiental', 'email_profissional', { width: 240 }),
            this.copia.coluna('Dt. Rescisão', 'dt_demissao', {
              width: 130,
              formatar: (v) => (v ? Utils.dateToPT(v) : ''),
            }),
            {
              Header: 'Status',
              accessor: 'status',
              width: 110,
              Cell: (row) => (
                <div className="action-cell" style={{ paddingLeft: '0px', textAlign: 'center' }}>
                  <Tag tone={row.value === 'ativo' ? 'green' : 'red'}>
                    {row.value === 'ativo' ? 'Ativo' : 'Desligado'}
                  </Tag>
                </div>
              ),
            },
            {
              Header: 'Tipo de Vínculo',
              accessor: 'tipo_vinculo',
              width: 130,
              Cell: (row) => (
                <div className="action-cell" style={{ textAlign: 'center' }}>
                  <Tag tone={toneForVinculo(row.value)} dot={false}>{row.value}</Tag>
                </div>
              ),
            },
            {
              Header: 'Férias',
              id: 'alertaFerias',
              width: 140,
              accessor: (linha) => {
                const alerta = this.state.alertasFerias[linha.cpf_funcionario];
                return alerta ? alerta.dias_restantes : null;
              },
              Cell: (row) => {
                const alerta = this.state.alertasFerias[row.original.cpf_funcionario];

                if (!alerta) {
                  return (
                    <div className="action-cell" style={{ textAlign: 'center', color: '#999' }}>
                      —
                    </div>
                  );
                }

                const vencido = alerta.dias_restantes < 0;

                return (
                  <div className="action-cell" style={{ textAlign: 'center' }}>
                    <Tag tone={vencido ? 'red' : 'orange'}>
                      {vencido
                        ? `Vencida há ${Math.abs(alerta.dias_restantes)}d`
                        : `Vence em ${alerta.dias_restantes}d`}
                    </Tag>
                  </div>
                );
              },
            },
          ]}
        />

        <CboAprendizModal
          open={this.state.cboModalAberto}
          onClose={() => this.setState({ cboModalAberto: false })}
          onAlterado={(lista) => this.setState({ regrasCbo: lista })}
        />

        <Form
          open={this.state.dialog}
          close={this.closeDialog}
          edicao={this.state.edicao}
          carregaTabela={this.getTableData}
          funcionario={this.state.funcionario}
        />
      </div>
    );
  }
}

export default Funcionarios;
